// RapidNewGrading.js
// "Classic" (NewGrading-style) grade display for a completed Rapid CCS case.
//
// Props:
//   grade          grade result object from api.gradeRapidCcs   (required)
//                  grade.GradeFeedback.SUMMARY, when present, renders a
//                  "Case Summary" block using the same
//                  .newGradingCaseSummary* classes/styles as NewGrading.js.
//   diagnosis      correct diagnosis string                     (required)
//   caseRow        the selected case row (for ID / title)       (optional)
//   onBackToCases  () => void                                   (required)
//   onRetry        () => void — retry this case                 (optional)
//   reviewLater    boolean — case is marked for review          (optional)
//   onToggleReviewLater  () => void — toggle the mark           (optional)
//   PreventiveCareGrading  boolean — when false, preventive care
//                  items are shown neutral with a "not graded" note (optional)
//
//   --- Highlighter -----------------------------------------------------
//   savedHighlights      array — previously-saved highlights to restore on
//                         mount, same shape produced by onHighlightsChange
//                         (optional)
//   onHighlightsChange   (highlights) => void — called whenever the
//                         highlight list changes (add / remove / clear) so
//                         the parent app can persist it however it likes
//                         (optional)
//
//   --- Reading Options ---------------------------------------------------
//   savedReadingOptions  object — initial { readingFontSize, readingFontFamily,
//                         readingLineHeight, readingLetterSpacing,
//                         readingColorScheme } to seed the panel with
//                         (optional)
//   onReadingOptionsChange (options) => void — called whenever any reading
//                         option changes (including reset) so the parent
//                         app can persist it however it likes (optional)
//
//   --- Feedback ------------------------------------------------------
//   onSubmitFeedback     (feedbackText) => Promise — called when the user
//                         submits the feedback form; the Feedback button is
//                         only shown when this is provided (optional)

import React from "react";
import "../NewGrading/NewGrading.css";
import "./RapidNewGrading.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faUser,
  faUsers,
  faCheck,
  faArrowRotateLeft,
  faHighlighter,
  faEraser,
} from "@fortawesome/free-solid-svg-icons";
import { faUserDoctorMessage } from "@fortawesome/pro-solid-svg-icons";
import MDSpinner from "react-md-spinner";

// ---------------------------------------------------------------------------
// Helpers (data shapes match RapidGradeScreen / api.gradeRapidCcs)
// ---------------------------------------------------------------------------

// Maps GradeColor ("Correct - Green", "Optional - Blue", "Incorrect - Red")
// to a verdict. Unknown/missing colors fall back to incorrect.
function gradeVerdict(item) {
  const c = (item.GradeColor || "").toLowerCase();
  if (c.indexOf("green") !== -1) return "correct";
  if (c.indexOf("blue") !== -1) return "neutral";
  if (c.indexOf("gray") !== -1 || c.indexOf("grey") !== -1) return "neutral";
  return "incorrect";
}

function pct(score, weight, digits) {
  if (typeof score !== "number" || typeof weight !== "number" || !weight) {
    return null;
  }
  const p = (score / weight) * 100;
  return p.toFixed(digits != null ? digits : 1);
}

// Splits a leading "[Tag] rest of text" prefix off a sequence-log entry.
function splitLogTag(entry) {
  const m = /^\[([^\]]+)\]\s*(.*)$/.exec(entry);
  if (m) return { tag: m[1], text: m[2] };
  return { tag: null, text: entry };
}

// Matches a GradeCategory string to one of the four rapid score/weight pairs.
function categoryScorePair(category, g) {
  const c = (category || "").toLowerCase();
  if (c.indexOf("diagnos") !== -1) {
    return { score: g.FinalDiagnosisGrade, weight: g.AdjustedWeightDiagnosis };
  }
  if (c.indexOf("treat") !== -1) {
    return { score: g.FinalTreatmentGrade, weight: g.AdjustedWeightTreatment };
  }
  if (c.indexOf("prevent") !== -1) {
    return {
      score: g.FinalPreventiveGrade,
      weight: g.AdjustedWeightPreventative,
    };
  }
  if (c.indexOf("appropriate") !== -1) {
    return {
      score: g.FinalAppropriateGrade,
      weight: g.AdjustedWeightAppropriate,
    };
  }
  console.log("Returning NULL for category ", category)
  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

class RapidNewGrading extends React.Component {
  constructor(props) {
    super(props);

    const savedReadingOptions = props.savedReadingOptions || {};

    this.state = {
      // --- Reading Options ---
      showReadingOptions: false,
      readingFontSize: savedReadingOptions.readingFontSize || "medium", // "small" | "medium" | "large" | "xlarge"
      readingFontFamily: savedReadingOptions.readingFontFamily || "sans", // "sans" | "serif" | "mono"
      readingLineHeight: savedReadingOptions.readingLineHeight || "normal", // "compact" | "normal" | "relaxed"
      readingLetterSpacing:
        savedReadingOptions.readingLetterSpacing || "normal", // "normal" | "wide"
      readingColorScheme: savedReadingOptions.readingColorScheme || "default", // "default" | "mono" | "sepia" | "cool" | "highcontrast"
      // --- Highlighter ---
      highlighterActive: false,
      // Each highlight is a plain serializable object — see NewGrading.js
      // for the full shape description.
      highlights: Array.isArray(props.savedHighlights)
        ? props.savedHighlights
        : [],
      // --- Feedback ---
      showFeedbackModal: false,
      feedbackText: "",
      feedbackLoading: false,
      feedbackSubmitted: false,
      feedbackError: null,
      // --- Order Log (rapid's ActionLog stand-in) ---
      selectedOrderLogItem: 0,
    };

    this.pageRef = React.createRef();
    this.HIGHLIGHT_REGISTRY_KEY = "rapidNewGradingHighlights";
    this.highlightApiSupported =
      typeof window !== "undefined" &&
      typeof window.Highlight === "function" &&
      typeof CSS !== "undefined" &&
      !!CSS.highlights;
  }

  componentDidMount() {
    this.applyHighlightsToDom();

    // --- Copy protection ---
    document.addEventListener("contextmenu", this.disableContextMenu);
    document.addEventListener("keydown", this.disableCopyCut);
    document.addEventListener("copy", this.disableCopyEvent);

    // --- Highlighter (mobile) ---
    // Touch-based selection (long-press + drag handles) is driven by the
    // browser/OS's native selection UI and does not reliably dispatch
    // "mouseup" (or even "touchend", once handles are being dragged) back to
    // our container. "selectionchange" is the one event that always fires
    // when the Selection object changes, regardless of whether a mouse or a
    // touch/handle-drag produced it, so it's the reliable cross-platform way
    // to detect "the user just finished selecting some text".
    document.addEventListener("selectionchange", this.handleSelectionChange);
  }

  componentDidUpdate(prevProps, prevState) {
    // Re-apply highlights whenever the highlight list changes or whenever the
    // underlying text/DOM could have changed (reading options all re-render
    // the highlighted containers).
    if (
      prevState.highlights !== this.state.highlights ||
      prevState.readingFontSize !== this.state.readingFontSize ||
      prevState.readingFontFamily !== this.state.readingFontFamily ||
      prevState.readingLineHeight !== this.state.readingLineHeight ||
      prevState.readingLetterSpacing !== this.state.readingLetterSpacing ||
      prevState.readingColorScheme !== this.state.readingColorScheme
    ) {
      this.applyHighlightsToDom();
    }
  }

  componentWillUnmount() {
    if (this.highlightApiSupported) {
      CSS.highlights.delete(this.HIGHLIGHT_REGISTRY_KEY);
    }

    // --- Copy protection ---
    document.removeEventListener("contextmenu", this.disableContextMenu);
    document.removeEventListener("keydown", this.disableCopyCut);
    document.removeEventListener("copy", this.disableCopyEvent);

    // --- Highlighter (mobile) ---
    document.removeEventListener("selectionchange", this.handleSelectionChange);
    if (this.selectionSettleTimer) {
      clearTimeout(this.selectionSettleTimer);
    }
  }

  // --- Copy protection ---
  disableContextMenu = (event) => event.preventDefault();

  disableCopyCut = (event) => {
    if (
      (event.ctrlKey || event.metaKey) &&
      (event.key === "c" || event.key === "x")
    ) {
      event.preventDefault();
    }
  };

  disableCopyEvent = (event) => {
    event.preventDefault();
  };

  // ============================================================
  // Order Log (rapid's ActionLog stand-in — same table/timeline layout
  // as ActionLog, but with the Day/Time columns replaced by the order
  // index, since OrderSequenceLog carries sequence only, no timestamps)
  // ============================================================

  updateSelectedOrderLogItem = (index, source) => {
    const selectedTimeline = document.getElementById(
      `rapid-order-log-timeline-${index}`,
    );
    if (selectedTimeline !== null && source !== "timeline") {
      selectedTimeline.scrollIntoView({ behavior: "smooth" });
    }
    const selectedRow = document.getElementById(`rapid-order-log-${index}`);
    if (selectedRow !== null && source !== "row") {
      selectedRow.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    this.setState({ selectedOrderLogItem: index });
  };

  // ============================================================
  // Reading Options
  // ============================================================

  getReadingStyle = (verdict = "neutral") => {
    const fontSizeMap = {
      small: "13px",
      medium: "15px",
      large: "17px",
      xlarge: "20px",
    };
    const fontFamilyMap = {
      sans: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      serif: "Georgia, 'Times New Roman', serif",
      mono: "'Courier New', Courier, monospace",
    };
    const lineHeightMap = { compact: "1.4", normal: "1.7", relaxed: "2.1" };
    const letterSpacingMap = { normal: "0px", wide: "0.04em" };

    // Color scheme overrides keyed by [scheme][verdict] → { background, color }
    const colorSchemes = {
      default: {
        neutral: { color: "var(--new-text-color)" },
      },
      mono: {
        correct: { backgroundColor: "#f0f0f0", color: "#1a1a1a" },
        incorrect: { backgroundColor: "#8a8a8a", color: "#f5f5f5" },
        optional: { backgroundColor: "#cbcbcb", color: "#1a1a1a" },
        neutral: { backgroundColor: "#e0e0e0", color: "#1a1a1a" },
      },
      sepia: {
        correct: { backgroundColor: "#f0e8cf", color: "#3b2f14" },
        incorrect: { backgroundColor: "#b89a70", color: "#2a1f0a" },
        optional: { backgroundColor: "#ddd0b0", color: "#3b2f14" },
        neutral: { backgroundColor: "#ede5cc", color: "#3b2f14" },
      },
      cool: {
        correct: { backgroundColor: "#e8f4fc", color: "#0d2a3a" },
        incorrect: { backgroundColor: "#7aafd4", color: "#0d1e2e" },
        optional: { backgroundColor: "#c0daf0", color: "#0d2a3a" },
        neutral: { backgroundColor: "#daeaf7", color: "#0d2a3a" },
      },
      highcontrast: {
        correct: { backgroundColor: "#003300", color: "#ccffcc" },
        incorrect: { backgroundColor: "#330000", color: "#ffcccc" },
        optional: { backgroundColor: "#1a1a00", color: "#ffffcc" },
        neutral: { backgroundColor: "#00134d", color: "#cce0ff" },
      },
    };

    const colorOverride =
      colorSchemes[this.state.readingColorScheme]?.[verdict] ?? {};

    return {
      fontSize: fontSizeMap[this.state.readingFontSize],
      fontFamily: fontFamilyMap[this.state.readingFontFamily],
      lineHeight: lineHeightMap[this.state.readingLineHeight],
      letterSpacing: letterSpacingMap[this.state.readingLetterSpacing],
      ...colorOverride,
    };
  };

  // Merge a partial change into reading-option state and notify the parent
  // (if it wants to persist it) with the resulting full option set.
  updateReadingOptions = (partial) => {
    this.setState(
      (s) => ({ ...s, ...partial }),
      () => {
        if (this.props.onReadingOptionsChange) {
          this.props.onReadingOptionsChange({
            readingFontSize: this.state.readingFontSize,
            readingFontFamily: this.state.readingFontFamily,
            readingLineHeight: this.state.readingLineHeight,
            readingLetterSpacing: this.state.readingLetterSpacing,
            readingColorScheme: this.state.readingColorScheme,
          });
        }
      },
    );
  };

  // ============================================================
  // Highlighter
  // ============================================================

  toggleHighlighter = () => {
    this.setState((s) => ({ highlighterActive: !s.highlighterActive }));
  };

  clearAllHighlights = () => {
    this.setState({ highlights: [] }, () => {
      if (this.props.onHighlightsChange) {
        this.props.onHighlightsChange(this.state.highlights);
      }
    });
  };

  // --- DOM helpers -------------------------------------------

  findHighlightContainer = (containerKey) => {
    const root = this.pageRef.current || document;
    const containers = root.querySelectorAll(
      "[data-highlight-container='true']",
    );
    for (let i = 0; i < containers.length; i++) {
      if (containers[i].dataset.highlightKey === containerKey) {
        return containers[i];
      }
    }
    return null;
  };

  getTextNodesIn = (container) => {
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null,
    );
    const nodes = [];
    let node;
    while ((node = walker.nextNode())) {
      nodes.push(node);
    }
    return nodes;
  };

  // Character offset of (node, offset) within container.textContent
  getOffsetWithin = (container, node, offset) => {
    try {
      const range = document.createRange();
      range.selectNodeContents(container);
      range.setEnd(node, offset);
      return range.toString().length;
    } catch (error) {
      return null;
    }
  };

  // Rebuild a Range from stored character offsets
  createRangeFromOffsets = (container, start, end) => {
    const textNodes = this.getTextNodesIn(container);
    let charCount = 0;
    let startNode = null;
    let startOffset = 0;
    let endNode = null;
    let endOffset = 0;

    for (let i = 0; i < textNodes.length; i++) {
      const node = textNodes[i];
      const length = node.textContent.length;
      if (startNode === null && start <= charCount + length) {
        startNode = node;
        startOffset = start - charCount;
      }
      if (startNode !== null && end <= charCount + length) {
        endNode = node;
        endOffset = end - charCount;
        break;
      }
      charCount += length;
    }

    if (!startNode || !endNode) {
      return null;
    }

    try {
      const range = document.createRange();
      range.setStart(startNode, startOffset);
      range.setEnd(endNode, endOffset);
      return range;
    } catch (error) {
      return null;
    }
  };

  // --- Applying highlights to the DOM ------------------------

  applyHighlightsToDom = () => {
    if (this.highlightApiSupported) {
      CSS.highlights.delete(this.HIGHLIGHT_REGISTRY_KEY);
    } else {
      this.clearHighlightMarks();
    }

    if (this.state.highlights.length === 0) {
      return;
    }

    const resolvedRanges = [];
    this.state.highlights.forEach((highlight) => {
      const container = this.findHighlightContainer(highlight.containerKey);
      if (!container) {
        return;
      }

      let range = this.createRangeFromOffsets(
        container,
        highlight.startOffset,
        highlight.endOffset,
      );

      // If the text at the stored offsets no longer matches (e.g. the
      // container's text changed between save and load), try to re-anchor
      // by searching for the stored text.
      if (!range || range.toString() !== highlight.text) {
        const fullText = container.textContent;
        const index = fullText.indexOf(highlight.text);
        if (index === -1) {
          return; // Can't place this highlight in the current DOM — skip it
        }
        range = this.createRangeFromOffsets(
          container,
          index,
          index + highlight.text.length,
        );
        if (!range) {
          return;
        }
      }

      resolvedRanges.push({ range, id: highlight.id });
    });

    if (resolvedRanges.length === 0) {
      return;
    }

    if (this.highlightApiSupported) {
      CSS.highlights.set(
        this.HIGHLIGHT_REGISTRY_KEY,
        new window.Highlight(...resolvedRanges.map((entry) => entry.range)),
      );
    } else {
      // Fallback: wrap ranges in <mark> elements. Process in reverse document
      // order so earlier offsets aren't invalidated by DOM splits.
      resolvedRanges
        .slice()
        .reverse()
        .forEach((entry) => this.wrapRangeInMarks(entry.range, entry.id));
    }
  };

  wrapRangeInMarks = (range, highlightId) => {
    const root =
      range.commonAncestorContainer.nodeType === Node.TEXT_NODE
        ? range.commonAncestorContainer.parentNode
        : range.commonAncestorContainer;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    const intersecting = [];
    let node;
    while ((node = walker.nextNode())) {
      if (range.intersectsNode(node)) {
        intersecting.push(node);
      }
    }

    // Reverse order so splitting text nodes doesn't shift earlier boundaries
    intersecting.reverse().forEach((textNode) => {
      const start = textNode === range.startContainer ? range.startOffset : 0;
      const end =
        textNode === range.endContainer
          ? range.endOffset
          : textNode.textContent.length;
      if (end <= start) {
        return;
      }

      let nodeToWrap = textNode;
      if (start > 0) {
        nodeToWrap = textNode.splitText(start);
      }
      if (end - start < nodeToWrap.textContent.length) {
        nodeToWrap.splitText(end - start);
      }

      const mark = document.createElement("mark");
      mark.className = "newGradingTextHighlight";
      mark.dataset.highlightId = highlightId;
      nodeToWrap.parentNode.insertBefore(mark, nodeToWrap);
      mark.appendChild(nodeToWrap);
    });
  };

  clearHighlightMarks = () => {
    const root = this.pageRef.current || document;
    root.querySelectorAll("mark.newGradingTextHighlight").forEach((mark) => {
      const parent = mark.parentNode;
      while (mark.firstChild) {
        parent.insertBefore(mark.firstChild, mark);
      }
      parent.removeChild(mark);
      parent.normalize();
    });
  };

  // --- Selection handling -------------------------------------

  // Extracts { clientX, clientY } from either a MouseEvent or a TouchEvent
  // (touchend events carry their coordinates on changedTouches, not on the
  // event itself).
  getEventPoint = (event) => {
    if (event.changedTouches && event.changedTouches.length > 0) {
      return {
        clientX: event.changedTouches[0].clientX,
        clientY: event.changedTouches[0].clientY,
      };
    }
    return { clientX: event.clientX, clientY: event.clientY };
  };

  // Turns the current, non-collapsed window selection into a stored
  // highlight (if it falls inside a highlight-able container). Shared by the
  // desktop mouseup path and the mobile selectionchange path.
  commitSelectionAsHighlight = (selection) => {
    const rawRange = selection.getRangeAt(0);
    const startElement =
      rawRange.startContainer.nodeType === Node.TEXT_NODE
        ? rawRange.startContainer.parentElement
        : rawRange.startContainer;
    const container =
      startElement &&
      startElement.closest &&
      startElement.closest("[data-highlight-container='true']");

    // Only text inside the designated containers can be highlighted
    if (!container) {
      selection.removeAllRanges();
      return;
    }

    // Clamp selections that run past the end of the container
    const range = rawRange.cloneRange();
    if (!container.contains(range.endContainer)) {
      range.setEnd(container, container.childNodes.length);
    }

    const startOffset = this.getOffsetWithin(
      container,
      range.startContainer,
      range.startOffset,
    );
    const endOffset = this.getOffsetWithin(
      container,
      range.endContainer,
      range.endOffset,
    );

    if (
      startOffset === null ||
      endOffset === null ||
      endOffset <= startOffset
    ) {
      selection.removeAllRanges();
      return;
    }

    const text = range.toString();
    if (!text.trim()) {
      selection.removeAllRanges();
      return;
    }

    const highlight = {
      id: `hl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      containerType: container.dataset.highlightType,
      containerKey: container.dataset.highlightKey,
      startOffset,
      endOffset,
      text,
      createdAt: new Date().toISOString(),
    };

    selection.removeAllRanges();
    this.setState(
      (s) => ({ highlights: [...s.highlights, highlight] }),
      () => {
        if (this.props.onHighlightsChange) {
          this.props.onHighlightsChange(this.state.highlights);
        }
      },
    );
  };

  // Desktop path: a real mouseup fires as soon as the mouse button is
  // released, so we can react immediately.
  handleHighlightMouseUp = (event) => {
    if (!this.state.highlighterActive) {
      return;
    }

    const selection = window.getSelection();
    if (!selection) {
      return;
    }

    // A plain click (no selection) on an existing highlight removes it
    if (selection.isCollapsed || selection.rangeCount === 0) {
      this.removeHighlightAtPoint(event);
      return;
    }

    this.commitSelectionAsHighlight(selection);
  };

  // Mobile path: dragging the native selection handles doesn't reliably
  // bubble mouseup/touchend up to our container (the drag happens on
  // browser/OS-owned UI, not our DOM), but "selectionchange" always fires as
  // the Selection object is updated. We debounce it so we only commit once
  // the selection has stopped changing for a moment (i.e. the user has
  // finished adjusting the handles) instead of firing on every intermediate
  // character the drag passes over.
  handleSelectionChange = () => {
    if (!this.state.highlighterActive) {
      return;
    }

    if (this.selectionSettleTimer) {
      clearTimeout(this.selectionSettleTimer);
    }

    this.selectionSettleTimer = setTimeout(() => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
        // Nothing selected (or selection was already collapsed, e.g. after
        // being committed elsewhere) — nothing to do here. Tap-to-remove is
        // handled by the touchend/mouseup handlers instead, so we don't try
        // to interpret collapsed selections here.
        return;
      }
      this.commitSelectionAsHighlight(selection);
    }, 300);
  };

  // Mobile path for tap-to-remove: touchend does fire reliably for simple
  // taps (as opposed to handle-drags), so it's a safe way to catch "the user
  // tapped an existing highlight to delete it" on touch devices.
  handleHighlightTouchEnd = (event) => {
    if (!this.state.highlighterActive) {
      return;
    }

    const selection = window.getSelection();
    if (selection && !selection.isCollapsed && selection.rangeCount > 0) {
      // A drag-selection is in progress/just finished — let
      // handleSelectionChange's debounce handle committing it so we don't
      // double-process the same selection.
      return;
    }

    this.removeHighlightAtPoint(event);
  };

  removeHighlightAtPoint = (event) => {
    // Fallback (<mark>) path: the tap/click target is the mark itself
    const markElement =
      event.target &&
      event.target.closest &&
      event.target.closest("mark.newGradingTextHighlight");
    if (markElement) {
      const id = markElement.dataset.highlightId;
      this.setState(
        (s) => ({
          highlights: s.highlights.filter((h) => h.id !== id),
        }),
        () => {
          if (this.props.onHighlightsChange) {
            this.props.onHighlightsChange(this.state.highlights);
          }
        },
      );
      return;
    }

    // CSS Custom Highlight API path: resolve the caret position under the
    // tap/click and match it against stored highlight ranges
    const container =
      event.target &&
      event.target.closest &&
      event.target.closest("[data-highlight-container='true']");
    if (!container) {
      return;
    }

    const { clientX, clientY } = this.getEventPoint(event);

    let node = null;
    let offset = 0;
    if (document.caretPositionFromPoint) {
      const position = document.caretPositionFromPoint(clientX, clientY);
      if (position) {
        node = position.offsetNode;
        offset = position.offset;
      }
    } else if (document.caretRangeFromPoint) {
      const caretRange = document.caretRangeFromPoint(clientX, clientY);
      if (caretRange) {
        node = caretRange.startContainer;
        offset = caretRange.startOffset;
      }
    }

    if (!node || !container.contains(node)) {
      return;
    }

    const globalOffset = this.getOffsetWithin(container, node, offset);
    if (globalOffset === null) {
      return;
    }

    const containerKey = container.dataset.highlightKey;
    const match = this.state.highlights.find(
      (h) =>
        h.containerKey === containerKey &&
        globalOffset >= h.startOffset &&
        globalOffset <= h.endOffset,
    );
    if (match) {
      this.setState(
        (s) => ({
          highlights: s.highlights.filter((h) => h.id !== match.id),
        }),
        () => {
          if (this.props.onHighlightsChange) {
            this.props.onHighlightsChange(this.state.highlights);
          }
        },
      );
    }
  };

  // ============================================================
  // Feedback
  // ============================================================

  submitFeedback = () => {
    if (!this.state.feedbackText.trim() || !this.props.onSubmitFeedback) {
      return;
    }

    this.setState({ feedbackLoading: true, feedbackError: null });

    Promise.resolve(this.props.onSubmitFeedback(this.state.feedbackText))
      .then(() => {
        this.setState({
          feedbackLoading: false,
          feedbackSubmitted: true,
          feedbackText: "",
        });
        setTimeout(() => {
          this.setState({
            showFeedbackModal: false,
            feedbackSubmitted: false,
          });
        }, 2000);
      })
      .catch((error) => {
        this.setState({
          feedbackLoading: false,
          feedbackError: "Failed to submit feedback. Please try again.",
        });
        console.log(error);
      });
  };

  closeFeedbackModal = () => {
    this.setState({
      showFeedbackModal: false,
      feedbackText: "",
      feedbackSubmitted: false,
      feedbackError: null,
    });
  };

  getImportanceLabel = (importance) => {
    const labels = {
      5: "Critical",
      4: "High",
      3: "Moderate",
      2: "Low",
      1: "Minor",
    };
    return labels[importance] || "";
  };

  // Same visual as NewGrading.renderImportanceIndicator.
  renderImportanceIndicator = (importance, variant) => {
    if (
      importance === undefined ||
      importance === null ||
      isNaN(importance) ||
      importance < 1 ||
      importance > 5 ||
      variant === "neutral"
    ) {
      return null;
    }

    const shadesByVariant = {
      incorrect: "#A32D2D",
      correct: "#27500A",
      neutral: "#1B4B52",
    };
    const emptyColorByVariant = {
      incorrect: "#F7C1C1",
      correct: "#C0DD97",
      neutral: "#C7DEE2",
    };

    const shade = shadesByVariant[variant] || shadesByVariant.neutral;
    const emptyColor =
      emptyColorByVariant[variant] || emptyColorByVariant.neutral;

    return (
      <div className="newGradingImportanceWrap">
        <div className="newGradingImportanceBar">
          {[1, 2, 3, 4, 5].map((level) => (
            <div
              key={level}
              className="newGradingImportanceSeg"
              style={{
                backgroundColor: level <= importance ? shade : emptyColor,
              }}
            />
          ))}
        </div>
        <span className="newGradingImportanceCaption">
          {this.getImportanceLabel(importance)}
        </span>
      </div>
    );
  };

  renderFooterButtons = () => {
    return (
      <div className="newGradingBackWrapper rapidNewGradingButtonRow">
        <button
          className="tertiaryButton newGradingBackButton"
          onClick={this.props.onBackToCases}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="newGradingBackIcon" />
          Back To Case List
        </button>
        {this.props.onToggleReviewLater && (
          <div
            className="newGradingCheckboxContainer"
            onClick={this.props.onToggleReviewLater}
            style={{ gridTemplateColumns: "30px auto" }}
          >
            <div className="newGradingCheckbox">
              {this.props.reviewLater && (
                <FontAwesomeIcon
                  icon={faCheck}
                  className="newGradingCheckboxIcon"
                />
              )}
            </div>
            <p className="newGradingCheckboxText">Review Later</p>
          </div>
        )}
      </div>
    );
  };

  render() {
    const g = this.props.grade || {};
    const items = Array.isArray(g.GradeOut) ? g.GradeOut : [];
    const diagnosis = this.props.diagnosis;
    const caseRow = this.props.caseRow || {};
    const preventiveOff = this.props.PreventiveCareGrading === false;

    // Group feedback items by GradeCategory, preserving first-seen order —
    // the rapid grade payload doesn't use the regular app's fixed section
    // titles, so the categories themselves define the sections.
    // Location / sequencing sections are intentionally excluded from this
    // view (the OrderSequenceLog below covers ordering).
    const order = [];
    const groups = {};
    items.forEach(function (it) {
      const cat = it.GradeCategory || "Other";
      if (cat.toLowerCase().indexOf("location") !== -1) return;
      if (!groups[cat]) {
        groups[cat] = [];
        order.push(cat);
      }
      groups[cat].push(it);
    });

    // Total achievable points (sum of the adjusted weights that exist).
    const totalWeight = [
      g.AdjustedWeightDiagnosis,
      g.AdjustedWeightTreatment,
      g.AdjustedWeightAppropriate,
      g.AdjustedWeightPreventative,
    ].reduce(function (sum, w) {
      return typeof w === "number" ? sum + w : sum;
    }, 0);

    const yourPct = pct(g.TotalGrade, totalWeight, 1);
    const avgPct = pct(g.AverageFirstAttempt, totalWeight, 1);

    const caseName =
      caseRow.NAME ||
      caseRow.Name ||
      caseRow.TITLE ||
      caseRow.Title ||
      caseRow.CaseName ||
      null;

    const seqLog = Array.isArray(g.OrderSequenceLog) ? g.OrderSequenceLog : [];

    return (
      <div className="rapidNewGradingScope">
        <div
          className={`newGradingPageWrapper${
            this.state.highlighterActive ? " newGradingHighlighterActive" : ""
          }`}
          ref={this.pageRef}
          onMouseUp={this.handleHighlightMouseUp}
          onTouchEnd={this.handleHighlightTouchEnd}
        >
          <style>{`
          ::highlight(${this.HIGHLIGHT_REGISTRY_KEY}) {
            background-color: rgba(255, 214, 10, 0.55);
          }
          mark.newGradingTextHighlight {
            background-color: rgba(255, 214, 10, 0.55);
            color: inherit;
            padding: 0;
          }
          .newGradingHighlighterActive [data-highlight-container='true'] {
            cursor: text;
          }
          .newGradingHighlighterActive [data-highlight-container='true'] ::selection {
            background-color: rgba(255, 214, 10, 0.45);
          }
        `}</style>
          {/* ── Top buttons ─────────────────────────────────────────── */}
          {this.renderFooterButtons()}

          {/* ── Case title ──────────────────────────────────────────── */}
          <div className="newGradingCaseTitleContainer">
            {caseRow.ID !== undefined && (
              <div className="newGradingCaseNumberContainer">
                <h1 className="newGradingCaseNumber">{caseRow.ID}</h1>
              </div>
            )}
            <div className="newGradingCaseTitleWrapper">
              <h2 className="newGradingCaseTitle">
                {caseName || diagnosis || "Rapid CCS Case"}
              </h2>
              <h3 className="newGradingCaseCompletionDate">
                Rapid CCS attempt
              </h3>
            </div>
            <div className="grading-button-block">
              <div className="highlightingContainer">
                <button
                  className="tertiaryButton newGradingBackButton"
                  onClick={this.toggleHighlighter}
                  title={
                    this.state.highlighterActive
                      ? "Highlighter on — select text to highlight it, click a highlight to remove it"
                      : "Turn on the highlighter"
                  }
                  style={
                    this.state.highlighterActive
                      ? { boxShadow: "0 0 0 2px rgba(255, 214, 10, 0.9) inset" }
                      : {}
                  }
                >
                  <FontAwesomeIcon
                    icon={faHighlighter}
                    className="newGradingBackIcon"
                  />
                  {this.state.highlighterActive
                    ? "Highlighter On"
                    : "Highlighter"}
                  {this.state.highlights.length > 0 &&
                    ` (${this.state.highlights.length})`}
                </button>
                {this.props.onRetry && (
                  <button
                    className="tertiaryButton newGradingBackButton"
                    onClick={this.props.onRetry}
                  >
                    <FontAwesomeIcon
                      icon={faArrowRotateLeft}
                      className="newGradingBackIcon"
                    />
                    Try Case Again
                  </button>
                )}
                {this.state.highlights.length > 0 && (
                  <button
                    className="tertiaryButton newGradingBackButton"
                    onClick={this.clearAllHighlights}
                    title="Remove all highlights"
                  >
                    <FontAwesomeIcon
                      icon={faEraser}
                      className="newGradingBackIcon"
                    />
                    Clear Highlights
                  </button>
                )}
              </div>
              {this.props.onSubmitFeedback && (
                <button
                  className="newGradingViewPreviousButton tertiaryButton"
                  onClick={() => this.setState({ showFeedbackModal: true })}
                >
                  Feedback
                </button>
              )}
              <div className="readingOptionsWrapper">
                <button
                  className="newGradingViewPreviousButton tertiaryButton readingOptionsToggle"
                  onClick={() =>
                    this.setState((s) => ({
                      showReadingOptions: !s.showReadingOptions,
                    }))
                  }
                >
                  <span className="readingOptionsIcon">Aa</span>
                  <span className="readingOptionsText">Reading Options</span>
                </button>
                {this.state.showReadingOptions && (
                  <>
                    <div
                      className="readingOptionsBackdrop"
                      onClick={() =>
                        this.setState({ showReadingOptions: false })
                      }
                    />
                    <div
                      className="readingOptionsPanel"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="readingOptionsSection">
                        <span className="readingOptionsLabel">Font Size</span>
                        <div className="readingOptionsRow">
                          {[
                            { key: "small", label: "S" },
                            { key: "medium", label: "M" },
                            { key: "large", label: "L" },
                            { key: "xlarge", label: "XL" },
                          ].map(({ key, label }) => (
                            <button
                              key={key}
                              className={`readingOptionChip ${this.state.readingFontSize === key ? "readingOptionChipActive" : ""}`}
                              onClick={() =>
                                this.updateReadingOptions({
                                  readingFontSize: key,
                                })
                              }
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="readingOptionsSection">
                        <span className="readingOptionsLabel">Typeface</span>
                        <div className="readingOptionsRow">
                          {[
                            { key: "sans", label: "Sans" },
                            { key: "serif", label: "Serif" },
                            { key: "mono", label: "Mono" },
                          ].map(({ key, label }) => (
                            <button
                              key={key}
                              className={`readingOptionChip ${this.state.readingFontFamily === key ? "readingOptionChipActive" : ""}`}
                              onClick={() =>
                                this.updateReadingOptions({
                                  readingFontFamily: key,
                                })
                              }
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="readingOptionsSection">
                        <span className="readingOptionsLabel">
                          Line Spacing
                        </span>
                        <div className="readingOptionsRow">
                          {[
                            { key: "compact", label: "Compact" },
                            { key: "normal", label: "Normal" },
                            { key: "relaxed", label: "Relaxed" },
                          ].map(({ key, label }) => (
                            <button
                              key={key}
                              className={`readingOptionChip ${this.state.readingLineHeight === key ? "readingOptionChipActive" : ""}`}
                              onClick={() =>
                                this.updateReadingOptions({
                                  readingLineHeight: key,
                                })
                              }
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="readingOptionsSection">
                        <span className="readingOptionsLabel">
                          Letter Spacing
                        </span>
                        <div className="readingOptionsRow">
                          {[
                            { key: "normal", label: "Normal" },
                            { key: "wide", label: "Wide" },
                          ].map(({ key, label }) => (
                            <button
                              key={key}
                              className={`readingOptionChip ${this.state.readingLetterSpacing === key ? "readingOptionChipActive" : ""}`}
                              onClick={() =>
                                this.updateReadingOptions({
                                  readingLetterSpacing: key,
                                })
                              }
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="readingOptionsSection">
                        <span className="readingOptionsLabel">
                          Color Scheme
                        </span>
                        <div className="readingOptionsColorRow">
                          {[
                            {
                              key: "default",
                              label: "Default",
                              swatches: ["#e2ffdd", "#ffdddd", "#d1ecf1"],
                            },
                            {
                              key: "mono",
                              label: "Mono",
                              swatches: ["#f0f0f0", "#8a8a8a", "#cbcbcb"],
                            },
                            {
                              key: "sepia",
                              label: "Sepia",
                              swatches: ["#f0e8cf", "#b89a70", "#ddd0b0"],
                            },
                            {
                              key: "cool",
                              label: "Cool",
                              swatches: ["#e8f4fc", "#7aafd4", "#c0daf0"],
                            },
                            {
                              key: "highcontrast",
                              label: "High Contrast",
                              swatches: ["#003300", "#330000", "#00134d"],
                            },
                          ].map(({ key, label, swatches }) => (
                            <button
                              key={key}
                              className={`readingColorChip ${this.state.readingColorScheme === key ? "readingColorChipActive" : ""}`}
                              onClick={() =>
                                this.updateReadingOptions({
                                  readingColorScheme: key,
                                })
                              }
                              title={label}
                            >
                              <span className="readingColorSwatches">
                                {swatches.map((bg, i) => (
                                  <span
                                    key={i}
                                    className="readingColorSwatch"
                                    style={{ backgroundColor: bg }}
                                  />
                                ))}
                              </span>
                              <span className="readingColorLabel">{label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <button
                        className="readingOptionsReset"
                        onClick={() =>
                          this.updateReadingOptions({
                            readingFontSize: "medium",
                            readingFontFamily: "sans",
                            readingLineHeight: "normal",
                            readingLetterSpacing: "normal",
                            readingColorScheme: "default",
                          })
                        }
                      >
                        Reset to defaults
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ── Score cards ─────────────────────────────────────────── */}
          <div
            className="newGradingCaseDetailsWrapper"
            data-highlight-container="true"
            data-highlight-key="caseDetails"
            data-highlight-type="caseDetails"
          >
            <div className="newGradingCaseDetailContainer">
              <div className="newGradingCaseDetailIconContainer">
                <FontAwesomeIcon
                  icon={faUser}
                  className="newGradingCaseDetailIcon"
                />
              </div>
              <h3 className="newGradingCaseDetailTitle">Your Score</h3>
              <h2 className="newGradingCaseDetailValue">
                {yourPct != null ? yourPct + "%" : "—"}
              </h2>
            </div>

            <div className="newGradingCaseDetailContainer">
              <div className="newGradingCaseDetailIconContainer">
                <FontAwesomeIcon
                  icon={faUsers}
                  className="newGradingCaseDetailIcon"
                />
              </div>
              <h3 className="newGradingCaseDetailTitle">
                Average First Attempt Score
              </h3>
              <h2 className="newGradingCaseDetailValue">
                {avgPct != null ? avgPct + "%" : "—"}
              </h2>
            </div>

            <div className="newGradingCaseDetailContainer">
              <div className="newGradingCaseDetailIconContainer">
                <FontAwesomeIcon
                  icon={faUserDoctorMessage}
                  className="newGradingCaseDetailIcon"
                />
              </div>
              <h3 className="newGradingCaseDetailTitle">Case Diagnosis</h3>
              <h2 className="newGradingCaseDetailValue">{diagnosis || "—"}</h2>
            </div>
          </div>

          {/* ── Grading sections ────────────────────────────────────── */}
          {order.map((cat, sectionIndex) => {
            console.log(cat);
            const pair = categoryScorePair(cat, g);
            const sectionPct = pair ? (pair.score ? pair.score : 0) : null;
            const weightPct =
              pair && totalWeight ? pct(pair.weight, totalWeight, 1) : null;
            const isPreventiveNotGraded =
              preventiveOff && cat.toLowerCase().indexOf("prevent") !== -1;
            console.log(pair, sectionPct, weightPct);
            return (
              <div
                className="newGradingFeedbackItemContainer"
                data-highlight-container="true"
                data-highlight-key={cat}
                data-highlight-type="feedbackItem"
                key={"rapidGradingSection-" + sectionIndex}
              >
                <h2 className="newGradingFeedbackItemTitle">
                  {cat}
                  {sectionPct != null && !isPreventiveNotGraded
                    ? ": " + sectionPct + "%"
                    : ""}
                </h2>
                {weightPct != null && !isPreventiveNotGraded && (
                  <h3 className="newGradingFeedbackItemValue">
                    Weight given for total score: {weightPct}% ({pair.weight}{" "}
                    pts)
                  </h3>
                )}
                {isPreventiveNotGraded && (
                  <h3 className="newGradingFeedbackItemValue">
                    Preventive care is not being graded for this case.
                  </h3>
                )}
                <div className="newGradingFeedbackItemContentWrapper">
                  {groups[cat].map((feedback, index) => {
                    const verdict = isPreventiveNotGraded
                      ? "neutral"
                      : gradeVerdict(feedback);
                    const eventName = (feedback.EventName || "").trim();
                    const isOptional =
                      eventName.indexOf("Optional Order") !== -1;
                    const wrapperClass =
                      verdict === "correct"
                        ? "newGradingFeedbackAnswerCorrect"
                        : verdict === "neutral" || isOptional
                          ? "newGradingFeedbackAnswerNeutral"
                          : "newGradingFeedbackAnswerIncorrect";

                    // Rapid items carry a single OrderName; tolerate the
                    // regular app's OrderNames / OrderNamesList too.
                    const orderNamesList = Array.isArray(
                      feedback.OrderNamesList,
                    )
                      ? feedback.OrderNamesList
                      : [];
                    const orderNames =
                      feedback.OrderName || feedback.OrderNames || null;

                    return (
                      <div
                        key={
                          "rapidGradingFeedback-" + sectionIndex + "-" + index
                        }
                        className={
                          "newGradingFeedbackAnswerWrapper " + wrapperClass
                        }
                        style={this.getReadingStyle(
                          verdict === "correct"
                            ? "correct"
                            : isOptional
                              ? "optional"
                              : verdict === "neutral"
                                ? "neutral"
                                : "incorrect",
                        )}
                      >
                        <div
                          className="newGradingFeedbackAnswerHeader"
                          style={{
                            marginBottom:
                              !orderNames &&
                              orderNamesList.length === 0 &&
                              !feedback.GradeExplanation
                                ? "0px"
                                : "15px",
                          }}
                        >
                          <div className="newGradingLeftBlock">
                            <h4 className="newGradingFeedbackAnswerTitle">
                              {eventName}
                            </h4>
                            {eventName.indexOf("Suboptimal") !== -1 && (
                              <div className="newGradingSuboptimalBlock">
                                <span className="newGradingSuboptimalSecond">
                                  Less Preferred
                                </span>
                              </div>
                            )}
                          </div>
                          {this.renderImportanceIndicator(
                            feedback.Importance,
                            verdict === "correct"
                              ? "correct"
                              : verdict === "neutral" || isOptional
                                ? "neutral"
                                : "incorrect",
                          )}
                        </div>
                        {orderNames && orderNamesList.length === 0 && (
                          <ul className="newGradingFeedbackAnswerItemList">
                            <li className="newGradingFeedbackAnswerItem">
                              {orderNames}
                            </li>
                          </ul>
                        )}
                        {orderNamesList.length !== 0 && (
                          <ul className="newGradingFeedbackAnswerItemList">
                            <li className="newGradingFeedbackAnswerItem">
                              {orderNamesList.join(" / ")}
                            </li>
                          </ul>
                        )}
                        {feedback.GradeExplanation && (
                          <p className="newGradingFeedbackReason">
                            <strong>Reason: </strong>
                            {feedback.GradeExplanation}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* ── Case summary ────────────────────────────────────────── */}
          {g.CaseSummary && (
            <div
              className="newGradingCaseSummaryContainer"
              style={this.getReadingStyle()}
              data-highlight-container="true"
              data-highlight-key="caseSummary"
              data-highlight-type="caseSummary"
            >
              <h2
                className="newGradingCaseSummaryTitle"
                style={{ color: "inherit" }}
              >
                Case Summary
              </h2>
              <p
                className="newGradingCaseSummaryText"
                style={{ color: "inherit" }}
              >
                {g.CaseSummary}
              </p>
            </div>
          )}

          {/* ── Order log (rapid's stand-in for the Action Log) ─────── */}
          {seqLog.length > 0 && (
            <div
              className="rapidNewGradingActionLogContainer"
              data-highlight-container="true"
              data-highlight-key="orderLog"
              data-highlight-type="orderLog"
              style={this.getReadingStyle()}
            >
              <div className="rapidNewGradingActionLogTableContainer">
                <h2 className="rapidNewGradingActionLogTitle">Order Log</h2>
                <div className="rapidNewGradingActionLogTableWrapper">
                  {seqLog.map((entry, index) => {
                    const parsed = splitLogTag(entry);
                    return (
                      <div
                        className={
                          "rapidNewGradingActionLogTableItem" +
                          (index === this.state.selectedOrderLogItem
                            ? " rapidNewGradingActionLogTableItemSelected"
                            : "")
                        }
                        onClick={() =>
                          this.updateSelectedOrderLogItem(index, "row")
                        }
                        id={`rapid-order-log-${index}`}
                        key={`rapid-order-log-row-${index}`}
                      >
                        <div className="rapidNewGradingActionLogIdxWrapper">
                          <h3 className="rapidNewGradingActionLogIdx">
                            #{index + 1}
                          </h3>
                        </div>
                        <p className="rapidNewGradingActionLogTableItemText">
                          {parsed.tag && (
                            <span className="rapidNewGradingActionLogTag">
                              {parsed.tag}
                            </span>
                          )}
                          {parsed.text}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="rapidNewGradingActionLogTimelineWrapper">
                <h2 className="rapidNewGradingActionLogTimelineHeader">
                  Order Log Timeline
                </h2>
                <div className="rapidNewGradingActionLogTimelineContainer">
                  <div
                    className={
                      "rapidNewGradingActionLogTopRowWrapper " +
                      (seqLog.length % 2 === 0
                        ? "rapidNewGradingActionLogEvenTopRowWrapper"
                        : "rapidNewGradingActionLogOddTopRowWrapper")
                    }
                  >
                    {seqLog.map((entry, index) => {
                      if (index % 2 !== 0) return null;
                      const parsed = splitLogTag(entry);
                      return (
                        <div
                          style={{ zIndex: index + 1 }}
                          key={`rapid-order-log-top-${index}`}
                          id={`rapid-order-log-timeline-${index}`}
                          className={
                            "rapidNewGradingActionLogTopRowItem" +
                            (index === this.state.selectedOrderLogItem
                              ? " rapidNewGradingActionLogRowItemSelected"
                              : "")
                          }
                          onClick={() =>
                            this.updateSelectedOrderLogItem(index, "timeline")
                          }
                        >
                          <h4
                            className={
                              "rapidNewGradingActionLogItemTitle" +
                              (index === this.state.selectedOrderLogItem
                                ? " rapidNewGradingActionLogItemTitleSelected"
                                : "")
                            }
                          >
                            {parsed.tag && (
                              <span className="rapidNewGradingActionLogTag">
                                {parsed.tag}
                              </span>
                            )}
                            {parsed.text}
                          </h4>
                          <p className="rapidNewGradingActionLogItemSub">
                            Order #{index + 1}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                  <div
                    className={
                      "rapidNewGradingActionLogBottomRowWrapper " +
                      (seqLog.length % 2 === 0
                        ? "rapidNewGradingActionLogEvenBottomRowWrapper"
                        : "rapidNewGradingActionLogOddBottomRowWrapper")
                    }
                  >
                    {seqLog.map((entry, index) => {
                      if (index % 2 === 0) return null;
                      const parsed = splitLogTag(entry);
                      return (
                        <div
                          style={{ zIndex: index + 1 }}
                          key={`rapid-order-log-bottom-${index}`}
                          id={`rapid-order-log-timeline-${index}`}
                          className={
                            "rapidNewGradingActionLogBottomRowItem" +
                            (index === this.state.selectedOrderLogItem
                              ? " rapidNewGradingActionLogRowItemSelected"
                              : "")
                          }
                          onClick={() =>
                            this.updateSelectedOrderLogItem(index, "timeline")
                          }
                        >
                          <h4
                            className={
                              "rapidNewGradingActionLogItemTitle" +
                              (index === this.state.selectedOrderLogItem
                                ? " rapidNewGradingActionLogItemTitleSelected"
                                : "")
                            }
                          >
                            {parsed.tag && (
                              <span className="rapidNewGradingActionLogTag">
                                {parsed.tag}
                              </span>
                            )}
                            {parsed.text}
                          </h4>
                          <p className="rapidNewGradingActionLogItemSub">
                            Order #{index + 1}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Bottom buttons ──────────────────────────────────────── */}
          {this.renderFooterButtons()}

          {/* ── Feedback modal ───────────────────────────────────────── */}
          {this.state.showFeedbackModal && (
            <div
              className="feedbackModalOverlay"
              onClick={this.closeFeedbackModal}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
            >
              <div
                className="feedbackModalContent"
                onClick={(e) => e.stopPropagation()}
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  padding: "24px",
                  width: "90%",
                  maxWidth: "500px",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                }}
              >
                <h2 style={{ margin: "0 0 16px 0", color: "#333" }}>
                  Send Feedback
                </h2>
                {this.state.feedbackSubmitted ? (
                  <div style={{ textAlign: "center", padding: "20px 0" }}>
                    <FontAwesomeIcon
                      icon={faCheck}
                      style={{
                        fontSize: "48px",
                        color: "#4CAF50",
                        marginBottom: "16px",
                      }}
                    />
                    <p style={{ color: "#333", fontSize: "16px" }}>
                      Thank you for your feedback!
                    </p>
                  </div>
                ) : (
                  <>
                    <textarea
                      value={this.state.feedbackText}
                      onChange={(e) =>
                        this.setState({ feedbackText: e.target.value })
                      }
                      placeholder="Share your thoughts, suggestions, or report issues..."
                      style={{
                        width: "100%",
                        minHeight: "120px",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                        fontSize: "14px",
                        resize: "vertical",
                        boxSizing: "border-box",
                        fontFamily: "inherit",
                      }}
                    />
                    {this.state.feedbackError && (
                      <p
                        style={{
                          color: "#f44336",
                          fontSize: "14px",
                          margin: "8px 0 0 0",
                        }}
                      >
                        {this.state.feedbackError}
                      </p>
                    )}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "12px",
                        marginTop: "16px",
                      }}
                    >
                      <button
                        className="tertiaryButton"
                        onClick={this.closeFeedbackModal}
                        style={{ padding: "10px 20px" }}
                      >
                        Cancel
                      </button>
                      <button
                        className="tertiaryButton"
                        onClick={this.submitFeedback}
                        disabled={
                          this.state.feedbackLoading ||
                          !this.state.feedbackText.trim()
                        }
                        style={{
                          padding: "10px 20px",
                          opacity:
                            this.state.feedbackLoading ||
                            !this.state.feedbackText.trim()
                              ? 0.6
                              : 1,
                        }}
                      >
                        {this.state.feedbackLoading ? (
                          <MDSpinner size={16} singleColor={"#fff"} />
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default RapidNewGrading;
